import React from 'react'
import theme from '../config/theme'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  itemsPerPage: number
  totalItems: number
  className?: string
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalItems,
  className = ''
}) => {
  // Calculate start and end item numbers
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show pages with ellipsis
      if (currentPage <= 3) {
        // Show first 4 pages + ellipsis + last page
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        // Show first page + ellipsis + last 4 pages
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // Show first page + ellipsis + current-1, current, current+1 + ellipsis + last page
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className={`w-full flex items-center justify-between ${className}`}>
      {/* Previous button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
          currentPage === 1
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-gray-50 cursor-pointer'
        }`}
        style={{
          color: theme.color.gray500,
          backgroundColor: theme.color.light100,
          borderColor: theme.color.light500,
        }}
      >
        <span className="mr-1">←</span>
        Previous
      </button>

      {/* Page numbers */}
      <div className='flex justify-center items-center gap-1 flex-1'>
        {pageNumbers.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span 
                className="px-2 py-2 text-sm"
                style={{ color: theme.color.gray200 }}
              >
                ...
              </span>
            ) : (
              <button
                onClick={() => onPageChange(page as number)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPage === page ? '' : 'hover:bg-gray-50 cursor-pointer'
                }`}
                style={{
                  color: currentPage === page ? theme.brandColor.red : theme.color.gray500,
                }}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Next button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
          currentPage === totalPages
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-gray-50 cursor-pointer'
        }`}
        style={{
          color: theme.color.gray500,
          backgroundColor: theme.color.light100,
          borderColor: theme.color.light500,
        }}
      >
        Next
        <span className="ml-1">→</span>
      </button>
    </div>
  )
}

export default Pagination
